/**
 * The one row of chrome above everything.
 *
 * Whole-photo and whole-selection actions only; anything that edits pixels
 * lives in the rail. Nothing here carries a caption, because a row of captions
 * is what the first build got wrong. Hover or focus explains, and every
 * explanation names the shortcut so this stops being a row you click.
 */

import { useRef } from 'react';
import { ACCEPTED_EXTENSIONS } from '@lumos/core';
import { Segmented } from './controls/Panel.tsx';
import { Tip } from './controls/Tooltip.tsx';
import { useSelectedPhoto, useStore, type SurfaceMode } from '../state/store.ts';

const ACCEPT = ACCEPTED_EXTENSIONS.map((extension) => `.${extension}`).join(',');

const APPLE = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent);
const MOD = APPLE ? '⌘' : 'Ctrl+';
const SHIFT = APPLE ? '⇧' : 'Shift+';

const MODES: { value: SurfaceMode; label: string }[] = [
  { value: 'guided', label: 'Guided' },
  { value: 'expert', label: 'Expert' },
];

const GROUP = { display: 'flex', gap: 6 } as const;

interface TopBarProps {
  onExport: () => void;
}

export function TopBar({ onExport }: TopBarProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const photo = useSelectedPhoto();

  const addFiles = useStore((state) => state.addFiles);
  const undo = useStore((state) => state.undo);
  const redo = useStore((state) => state.redo);
  const copySettings = useStore((state) => state.copySettings);
  const pasteSettings = useStore((state) => state.pasteSettings);
  const syncToSelection = useStore((state) => state.syncToSelection);
  const resetRecipe = useStore((state) => state.resetRecipe);
  const clipboard = useStore((state) => state.clipboard);
  const selectionCount = useStore((state) => state.selection.length);
  const manyPhotos = useStore((state) => state.photos.length > 1);
  const mode = useStore((state) => state.mode);
  const setMode = useStore((state) => state.setMode);
  const showHelp = useStore((state) => state.showHelp);
  const toggleHelp = useStore((state) => state.toggleHelp);
  const chatOpen = useStore((state) => state.chatOpen);
  const toggleChat = useStore((state) => state.toggleChat);

  const canUndo = photo !== null && photo.historyIndex > 0;
  const canRedo = photo !== null && photo.historyIndex < photo.history.length - 1;
  const targets = selectionCount - 1;

  return (
    <header className="topbar">
      <span className="wordmark" role="img" aria-label="Lumos">
        LUM<span>O</span>S
      </span>

      <Tip body="Add raw files or JPEGs to this session.">
        <button type="button" className="btn" onClick={() => inputRef.current?.click()}>
          Import
        </button>
      </Tip>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        hidden
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => {
          const files = Array.from(event.target.files ?? []);
          if (files.length > 0) addFiles(files);
          event.target.value = '';
        }}
      />

      <div style={GROUP}>
        <Tip title="Undo" body={`Step back one change. ${MOD}Z`}>
          <button
            type="button"
            className="btn icon"
            onClick={undo}
            disabled={!canUndo}
            aria-label={`Undo, ${MOD}Z`}
          >
            <span aria-hidden="true">↺</span>
          </button>
        </Tip>
        <Tip title="Redo" body={`Step forward again. ${SHIFT}${MOD}Z`}>
          <button
            type="button"
            className="btn icon"
            onClick={redo}
            disabled={!canRedo}
            aria-label={`Redo, ${SHIFT}${MOD}Z`}
          >
            <span aria-hidden="true">↻</span>
          </button>
        </Tip>
      </div>

      {manyPhotos ? (
        <div style={GROUP}>
          <Tip body={`Picks up this photo’s settings. ${MOD}C`}>
            <button type="button" className="btn" onClick={copySettings} disabled={!photo}>
              Copy
            </button>
          </Tip>
          <Tip
            body={
              clipboard
                ? `Puts the settings from ${clipboard.sourceFilename} on this photo. ${MOD}V`
                : 'Copy a photo’s settings first.'
            }
          >
            <button
              type="button"
              className="btn"
              onClick={pasteSettings}
              disabled={!photo || !clipboard}
            >
              Paste
            </button>
          </Tip>
          <Tip
            body={
              targets > 0
                ? `Puts these settings on the other ${targets} selected. ${SHIFT}${MOD}S`
                : 'Pick more photos in the filmstrip first.'
            }
          >
            <button
              type="button"
              className="btn"
              onClick={syncToSelection}
              disabled={!photo || targets < 1}
            >
              {targets > 0 ? `Sync to ${targets}` : 'Sync'}
            </button>
          </Tip>
        </div>
      ) : null}

      <span className="topbar-spacer" />

      {/* Without this the assistant column can be folded away by the shortcut
          and only the shortcut can bring it back. */}
      <Tip body={`Show or hide the assistant column. ${MOD}\\`}>
        <button
          type="button"
          className={`btn${chatOpen ? ' active' : ''}`}
          onClick={toggleChat}
          aria-pressed={chatOpen}
        >
          Assistant
        </button>
      </Tip>

      <Tip body="Show explanations under each control.">
        <button
          type="button"
          className={`btn icon${showHelp ? ' active' : ''}`}
          onClick={toggleHelp}
          aria-pressed={showHelp}
          aria-label="Show explanations under each control, question mark key"
        >
          <span aria-hidden="true">?</span>
        </button>
      </Tip>

      <Tip as="div" body="Guided keeps to the controls most photos need. Expert shows all of them.">
        <Segmented value={mode} options={MODES} onChange={setMode} ariaLabel="Control layout" />
      </Tip>

      <Tip body="Back to how the camera shot it. R">
        <button type="button" className="btn" onClick={resetRecipe} disabled={!photo?.edited}>
          Reset
        </button>
      </Tip>

      <Tip body={`Write a JPEG, PNG or WebP. ${MOD}E`}>
        <button type="button" className="btn ember" onClick={onExport} disabled={!photo}>
          Export
        </button>
      </Tip>
    </header>
  );
}
